import React, { useState } from 'react';
import { X, AlertTriangle, Ban, Loader } from 'lucide-react';

const SuspensionModal = ({ isOpen, user, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState(7); // Default 7 days
  const [isSuspending, setIsSuspending] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !user) return null;

  const isValidForm = reason.trim() && duration > 0;

  const handleConfirm = async () => {
    if (!isValidForm) {
      setError('Please provide a reason and duration');
      return;
    }

    if (duration > 365) {
      setError('Suspension duration cannot exceed 365 days. Use "Permanent Ban" for permanent suspensions.');
      return;
    }
    
    setError('');
    setIsSuspending(true);
    try {
      await onConfirm(reason, duration);
    } catch (err) {
      setError(err.message || 'Failed to suspend user');
    } finally {
      setIsSuspending(false);
    }
  };

  const handleCancel = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setReason('');
    setDuration(7);
    setError('');
    if (onCancel) {
      onCancel();
    }
  };

  const suspensionEndDate = new Date();
  suspensionEndDate.setDate(suspensionEndDate.getDate() + parseInt(duration || 0));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" onClick={(e) => handleCancel(e)}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-red-200 bg-red-50">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <Ban className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-red-900">Suspend User Account</h2>
              <p className="text-sm text-red-700">Temporary account suspension</p>
            </div>
          </div>
          <button
            onClick={(e) => handleCancel(e)}
            disabled={isSuspending}
            className="p-2 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5 text-red-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* User Information */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">User to be suspended:</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Name:</span>
                <span className="text-sm font-medium text-gray-900">{user.full_name || user.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Email:</span>
                <span className="text-sm font-medium text-gray-900">{user.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Role:</span>
                <span className="text-sm font-medium text-gray-900 uppercase">{user.role}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Current Status:</span>
                <span className="text-sm font-medium text-gray-900 uppercase">
                  {user.moderation_status?.status || 'active'}
                </span>
              </div>
            </div>
          </div>

          {/* Suspension Info */}
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
            <div className="flex">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-2">Temporary Suspension</p>
                <p>This action will:</p>
                <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
                  <li>Prevent user from logging in</li>
                  <li>Stop all job posting/applications (for VAs/Employers)</li>
                  <li>Block messaging and communications</li>
                  <li>Automatically reactivate after the suspension period ends</li>
                  <li>Send notification email to user with reactivation date</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            {/* Suspension Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Suspension <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError('');
                }}
                placeholder="Explain why this user is being suspended (e.g., 'Violating community guidelines regarding professional conduct')"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={4}
                maxLength={500}
              />
              <div className="text-xs text-gray-500 mt-1">
                {reason.length}/500 characters
              </div>
            </div>

            {/* Suspension Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Suspension Duration <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <input
                    type="number"
                    value={duration}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (val > 0 && val <= 365) {
                        setDuration(val);
                        setError('');
                      } else if (val > 365) {
                        setError('Maximum suspension duration is 365 days');
                      }
                    }}
                    min="1"
                    max="365"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="text-xs text-gray-500 mt-1">Days (1-365)</div>
                </div>
                <div>
                  <input
                    type="text"
                    value={suspensionEndDate.toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                    disabled
                    className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed text-gray-600"
                  />
                  <div className="text-xs text-gray-500 mt-1">Reactivation Date</div>
                </div>
              </div>

              {/* Quick Duration Options */}
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="text-xs text-gray-600 self-center">Quick select:</span>
                {[
                  { label: '24h', days: 1 },
                  { label: '3 days', days: 3 },
                  { label: '7 days', days: 7 },
                  { label: '14 days', days: 14 },
                  { label: '30 days', days: 30 },
                  { label: '90 days', days: 90 }
                ].map(option => (
                  <button
                    key={option.days}
                    onClick={() => {
                      setDuration(option.days);
                      setError('');
                    }}
                    className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                      duration === option.days
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Summary</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• User will be suspended for <strong>{duration} day{duration > 1 ? 's' : ''}</strong></li>
              <li>• Account will automatically reactivate on <strong>{suspensionEndDate.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</strong></li>
              <li>• Suspension notification will be sent to <strong>{user.email}</strong></li>
              <li>• Reason: <strong>{reason || '(Not provided)'}</strong></li>
            </ul>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t bg-gray-50 p-6 flex justify-end space-x-3">
          <button
            onClick={(e) => handleCancel(e)}
            disabled={isSuspending}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isValidForm || isSuspending}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSuspending ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>Suspending...</span>
              </>
            ) : (
              <>
                <Ban className="w-4 h-4" />
                <span>Confirm Suspension</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuspensionModal;
